export async function generateQuoteImage(data: {
  text: string;
  category: string;
  author?: string;
  length?: 'short' | 'medium' | 'long';
}): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  
  canvas.width = 1200;
  canvas.height = 630;
  
  // Create dynamic gradient based on category
  const gradients: Record<string, string[]> = {
    'प्रेरणा': ['#f3e8ff', '#fce7f3', '#f3e8ff'],
    'सफलता': ['#e0f2fe', '#dbeafe', '#e0f2fe'],
    'जीवन': ['#dcfce7', '#d1fae5', '#dcfce7'],
    'जीवन दर्शन': ['#fff7ed', '#fef3c7', '#fff7ed'],
    'प्रेम': ['#ffe4e6', '#fce7f3', '#ffe4e6'],
    'परिवार': ['#f3e8ff', '#e0f2fe', '#f3e8ff'],
    'शिक्षा': ['#dbeafe', '#e0f2fe', '#dbeafe'],
    'मित्रता': ['#dcfce7', '#f0fdf4', '#dcfce7'],
    'शान्ति': ['#f8fafc', '#f1f5f9', '#f8fafc'],
    'ज्ञान': ['#fef3c7', '#fff7ed', '#fef3c7']
  };

  const colors = gradients[data.category] || ['#f3e8ff', '#fce7f3', '#f3e8ff'];
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, colors[0]);
  gradient.addColorStop(0.5, colors[1]);
  gradient.addColorStop(1, colors[2]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Create elegant pattern background
  ctx.save();
  ctx.globalAlpha = 0.03;
  for (let i = 0; i < canvas.width; i += 40) {
    for (let j = 0; j < canvas.height; j += 40) {
      ctx.beginPath();
      ctx.arc(i, j, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();

  // Add subtle mandala watermark
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(-Math.PI / 6);
  ctx.globalAlpha = 0.04;
  
  const radius = 200;
  const petals = 8;
  for (let i = 0; i < petals; i++) {
    ctx.rotate((Math.PI * 2) / petals);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(radius / 2, -radius / 4, radius, 0);
    ctx.quadraticCurveTo(radius / 2, radius / 4, 0, 0);
    ctx.fill();
  }
  ctx.restore();

  // Add small watermark text pattern
  ctx.save();
  ctx.font = 'bold 16px Arial';
  ctx.fillStyle = 'rgba(147, 51, 234, 0.05)';
  ctx.textAlign = 'center';
  
  for (let i = -2; i <= 2; i++) {
    for (let j = -2; j <= 2; j++) {
      const x = canvas.width / 2 + i * 200;
      const y = canvas.height / 2 + j * 100;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-Math.PI / 6);
      ctx.fillText('WWW.ABH.COM.NP', 0, 0);
      ctx.restore();
    }
  }
  ctx.restore();

  // Add decorative frame
  ctx.strokeStyle = 'rgba(147, 51, 234, 0.1)';
  ctx.lineWidth = 2;
  const margin = 20;
  const cornerRadius = 10;

  ctx.beginPath();
  ctx.moveTo(margin + cornerRadius, margin);
  ctx.lineTo(canvas.width - margin - cornerRadius, margin);
  ctx.quadraticCurveTo(canvas.width - margin, margin, canvas.width - margin, margin + cornerRadius);
  ctx.lineTo(canvas.width - margin, canvas.height - margin - cornerRadius);
  ctx.quadraticCurveTo(canvas.width - margin, canvas.height - margin, canvas.width - margin - cornerRadius, canvas.height - margin);
  ctx.lineTo(margin + cornerRadius, canvas.height - margin);
  ctx.quadraticCurveTo(margin, canvas.height - margin, margin, canvas.height - margin - cornerRadius);
  ctx.lineTo(margin, margin + cornerRadius);
  ctx.quadraticCurveTo(margin, margin, margin + cornerRadius, margin);
  ctx.stroke();

  // Add content with enhanced styling
  ctx.textAlign = 'center';
  
  // Category badge with gradient
  ctx.save();
  const badgeGradient = ctx.createLinearGradient(
    canvas.width/2 - 100, 80,
    canvas.width/2 + 100, 80
  );
  badgeGradient.addColorStop(0, '#7c3aed');
  badgeGradient.addColorStop(1, '#db2777');
  ctx.fillStyle = badgeGradient;
  ctx.font = 'bold 28px Arial';
  ctx.fillText(data.category.toUpperCase(), canvas.width/2, 80);
  ctx.restore();

  // Quote text with dynamic styling
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = '#1f2937';
  
  const fontSize = data.length === 'short' ? 48 : 
                  data.length === 'medium' ? 36 : 28;
  ctx.font = `${fontSize}px Georgia`;
  
  const words = data.text.split(' ');
  let lines = [];
  let currentLine = '';
  const maxWidth = canvas.width - 160;
  
  for (const word of words) {
    const testLine = currentLine + word + ' ';
    const metrics = ctx.measureText(testLine);
    
    if (metrics.width > maxWidth) {
      lines.push(currentLine);
      currentLine = word + ' ';
    } else {
      currentLine = testLine;
    }
  }
  lines.push(currentLine);
  
  const lineHeight = fontSize * 1.4;
  const totalTextHeight = lines.length * lineHeight;
  const startY = (canvas.height - totalTextHeight) / 2;
  
  lines.forEach((line, index) => {
    ctx.fillText(line.trim(), canvas.width/2, startY + (index * lineHeight));
  });
  ctx.restore();

  // Author attribution with elegant styling
  if (data.author) {
    ctx.save();
    const authorGradient = ctx.createLinearGradient(
      canvas.width/2 - 50,
      startY + totalTextHeight + 60,
      canvas.width/2 + 50,
      startY + totalTextHeight + 60
    );
    authorGradient.addColorStop(0, '#4b5563');
    authorGradient.addColorStop(1, '#6b7280');
    ctx.fillStyle = authorGradient;
    ctx.font = 'italic 32px Georgia';
    ctx.fillText(`- ${data.author}`, canvas.width/2, startY + totalTextHeight + 60);
    ctx.restore();
  }

  // Add elegant footer with website URL
  ctx.save();
  const footerGradient = ctx.createLinearGradient(
    canvas.width/2 - 100,
    canvas.height - 40,
    canvas.width/2 + 100,
    canvas.height - 40
  );
  footerGradient.addColorStop(0, '#6b7280');
  footerGradient.addColorStop(1, '#9ca3af');
  ctx.fillStyle = footerGradient;
  ctx.font = '24px Arial';
  ctx.fillText('www.abh.com.np', canvas.width/2, canvas.height - 40);
  ctx.restore();

  return canvas.toDataURL('image/png', 1.0);
}

export async function generateNameMatchImage(data: {
  name: string;
  matches: Array<{
    name: string;
    compatibility: number;
    description: string;
  }>;
}): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  
  canvas.width = 1200;
  canvas.height = 630;
  
  // Create dynamic gradient background
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#f3e8ff');
  gradient.addColorStop(0.5, '#fce7f3');
  gradient.addColorStop(1, '#f3e8ff');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Add elegant pattern background
  ctx.save();
  ctx.globalAlpha = 0.03;
  for (let i = 0; i < canvas.width; i += 40) {
    for (let j = 0; j < canvas.height; j += 40) {
      ctx.beginPath();
      ctx.arc(i, j, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();

  // Add subtle decorative elements
  ctx.save();
  ctx.globalAlpha = 0.05;
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const maxRadius = 300;
  
  for (let radius = 50; radius <= maxRadius; radius += 50) {
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();

  // Add small watermark pattern
  ctx.save();
  ctx.font = 'bold 16px Arial';
  ctx.fillStyle = 'rgba(147, 51, 234, 0.05)';
  ctx.textAlign = 'center';
  
  for (let i = -2; i <= 2; i++) {
    for (let j = -2; j <= 2; j++) {
      const x = canvas.width / 2 + i * 200;
      const y = canvas.height / 2 + j * 100;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-Math.PI / 6);
      ctx.fillText('WWW.ABH.COM.NP', 0, 0);
      ctx.restore();
    }
  }
  ctx.restore();

  // Add main content
  ctx.save();
  // Title with gradient
  const titleGradient = ctx.createLinearGradient(0, 80, canvas.width, 80);
  titleGradient.addColorStop(0, '#7c3aed');
  titleGradient.addColorStop(1, '#db2777');
  ctx.fillStyle = titleGradient;
  ctx.font = 'bold 48px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(`Perfect Matches for "${data.name}"`, canvas.width/2, 80);

  // Draw matches in a grid
  const startY = 150;
  const padding = 20;
  const matchWidth = (canvas.width - padding * 4) / 3;
  const matchHeight = 180;

  data.matches.forEach((match, index) => {
    const row = Math.floor(index / 3);
    const col = index % 3;
    const x = padding + col * (matchWidth + padding);
    const y = startY + row * (matchHeight + padding);

    // Draw match card with gradient background
    const cardGradient = ctx.createLinearGradient(x, y, x + matchWidth, y);
    cardGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    cardGradient.addColorStop(1, 'rgba(255, 255, 255, 0.95)');
    ctx.fillStyle = cardGradient;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.roundRect(x, y, matchWidth, matchHeight, 10);
    ctx.fill();

    // Draw match content
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 24px Arial';
    ctx.fillText(match.name, x + matchWidth/2, y + 40);

    ctx.font = '18px Arial';
    ctx.fillStyle = '#4b5563';
    const words = match.description.split(' ');
    let line = '';
    let lineY = y + 80;
    
    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > matchWidth - 40) {
        ctx.fillText(line, x + matchWidth/2, lineY);
        line = word + ' ';
        lineY += 25;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x + matchWidth/2, lineY);

    // Draw compatibility score with gradient
    const scoreGradient = ctx.createLinearGradient(
      x + matchWidth/2 - 50,
      y + matchHeight - 30,
      x + matchWidth/2 + 50,
      y + matchHeight - 30
    );
    scoreGradient.addColorStop(0, '#7c3aed');
    scoreGradient.addColorStop(1, '#db2777');
    ctx.fillStyle = scoreGradient;
    ctx.font = 'bold 20px Arial';
    ctx.fillText(`${match.compatibility}% Match`, x + matchWidth/2, y + matchHeight - 30);
  });
  ctx.restore();

  // Add website URL at bottom
  ctx.save();
  const footerGradient = ctx.createLinearGradient(
    canvas.width/2 - 100,
    canvas.height - 40,
    canvas.width/2 + 100,
    canvas.height - 40
  );
  footerGradient.addColorStop(0, '#6b7280');
  footerGradient.addColorStop(1, '#9ca3af');
  ctx.fillStyle = footerGradient;
  ctx.font = '24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('www.abh.com.np', canvas.width/2, canvas.height - 40);
  ctx.restore();

  return canvas.toDataURL('image/png', 1.0);
}